import {
  CountryIsoCode,
  diallingCodes
} from '@toasttab/buffet-pui-country-utilities'
import {
  CountryCode,
  isSupportedCountry,
  parsePhoneNumber
} from 'libphonenumber-js'
import { PhoneNumber } from '@toasttab/buffet-pui-phone-input'

/**
 * @param country Country code to validate
 * @returns Country code of the restaurant if the restaurant has a country code and it is a code supported by libphonenumber-js, otherwise default to 'US'
 */
export const asValidPhoneCountryCode = (countryCode: any) =>
  isSupportedCountry(countryCode) ? countryCode : ('US' as CountryCode)

// Helper function to convert phone country code (like "+353") to ISO code (like "IE")
export const phoneCountryCodeToIso = (
  phoneCountryCode?: string,
  restaurantCountry?: string
): CountryIsoCode | undefined => {
  if (!phoneCountryCode) return undefined

  const cleanCode = phoneCountryCode.replace('+', '')

  // Handle North American dialing code (+1)
  if (cleanCode === '1') {
    // Only Canadian restaurants get Canadian formatting for +1
    return restaurantCountry === 'CA' ? 'CA' : 'US'
  }

  // Look up country by dialing code
  const matchingCountry = Object.entries(diallingCodes).find(
    ([_, diallingCode]) => diallingCode === cleanCode
  )

  if (matchingCountry) {
    return matchingCountry[0] as CountryIsoCode
  }

  // Unknown dialing code - fall back to restaurant country or US
  return (restaurantCountry || 'US') as CountryIsoCode
}

// Convert string phone to PhoneNumber format
export const convertToPhoneNumber = (
  phone?: string | null,
  phoneCountryCode?: string,
  restaurantCountry?: string
): PhoneNumber | undefined => {
  if (!phone) return undefined

  // Convert phone country code to ISO code
  const countryIsoCode =
    phoneCountryCodeToIso(phoneCountryCode, restaurantCountry) || 'US'
  // countryCode should be the dialing code (like "353"), not the ISO code
  const dialingCode =
    phoneCountryCode?.replace('+', '') ||
    diallingCodes[countryIsoCode as keyof typeof diallingCodes]

  return {
    nationalNumber: stripCountryCode(phone),
    countryCode: dialingCode, // The dialing code like "353"
    countryIsoCode: countryIsoCode // The ISO code like "IE"
  } as PhoneNumber
}

export const stripCountryCode = (phone: string | undefined | null) => {
  if (!phone) {
    return ''
  }

  const stripped = phone.replace(/\D/g, '')

  if (stripped.length > 10) {
    return stripped.slice(-10)
  } else {
    return phone
  }
}

export const formatPhoneNumber = (
  phoneNumber: string,
  phoneCountryCode?: string,
  restaurantCountry?: string
): string | undefined => {
  const phoneIsoCode = phoneCountryCodeToIso(
    phoneCountryCode,
    restaurantCountry
  )
  return formatPhoneNumberFromCountryIso(phoneNumber, phoneIsoCode ?? 'US')
}

export const formatPhoneNumberFromCountryIso = (
  phoneNumber: string,
  countryIso?: CountryIsoCode
): string | undefined => {
  const countryCodeWithFallback = countryIso ?? 'US'
  // Use libphonenumber-js to format the phone number
  const parsedResult = parsePhoneNumber(phoneNumber, countryCodeWithFallback)
  if (countryCodeWithFallback === 'US' || countryCodeWithFallback === 'CA') {
    return parsedResult?.formatNational()
  } else {
    return parsedResult?.formatInternational()
  }
}
